import { OrderDirection } from "../models/DataTableFilter.model";
import moment from 'moment';

/**
 * myArray = [{id: 2, name: 'Anna'}, {id: 3, name: 'Peter}] -> addIfNotExists(myArray, {id:55, name: 'JSmith'} , x => x.id === 55);
 * @param array
 * @param predicate
 */
export function addIfNotExists<T>(array: T[], item: T, predicate: (value: T) => boolean, addNewElemOnFirstPosition = false): void {
    if (array) {
        const index = array.findIndex(predicate);
        if (index <= -1) {
            if (addNewElemOnFirstPosition) {
                array.unshift(item);
            } else {
                array.push(item);
            }
        }
    }
}

/**
 * myArray = [{id: 2, name: 'Anna'}, {id: 3, name: 'Peter}] -> removeIfExists(myArray, x => x.id === 3);
 * @param array
 * @param predicate
 */
export function removeIfExists<T>(array: T[], predicate: (value: T) => boolean): void {
    if (array) {
        const index = array.findIndex(predicate);
        if (index > -1) {
            array.splice(index, 1);
        }
    }
}

/**
* Example: myArray = [{id: 2, name: 'Anna'}, {id: 3, name: 'Peter'}, {id: 4, name: 'Peter}] -> removeAllIfExist(myArray, x => x.name === 'Peter'); 
* Result: myArray = [{id: 2, name: 'Anna'}]
* @param array
* @param predicate
*/
export function removeAllIfExist<T>(array: T[], predicate: (value: T) => boolean): void {
   if (array) {
       let index = array.findIndex(predicate);
       while (index > -1) {
           array.splice(index, 1);
           index = array.findIndex(predicate);
       }
   }
}

/**
 * Example myArray = [{id: 1, age: 31}, {id: 2, age: 25}]; addOrReplace(myArray, {id: 2, age: 30}, x => x.id == 2)
 * @param array 
 * @param item 
 * @param predicate 
 */
export function addOrReplace<T>(array: T[], item: T, predicate: (value: T) => boolean): void {
    removeIfExists(array, predicate);
    addIfNotExists(array, item, predicate);
}

/**
 * Example myArray = [{id: 1, age: 31}, {id: 2, age: 25}]
 * singleOrDefault(myArray, x => x.age > 20) -> Error since there are two elements
 * singleOrDefault(myArray, x => x.age === 31) -> {id: 1, age: 31}
 * singleOrDefault(myArray, x => x.age === 32) -> null
 * @param array 
 * @param predicate 
 */
export function singleOrDefault<T>(array: T[], predicate: (value: T) => boolean): T {
    if (array) {
        const results: Array<T> = array.filter(predicate);
        if (results.length > 1) {
            throw new Error("There is more than one matching element");
        }
        if (results.length == 1) {
            return results[0];
        }
    }
    return null;
}

/**
 * Example myArray = [{id: 1, age: 31}, {id: 2, age: 25}]
 * firstOrDefault(myArray) -> {id: 1, age: 31}
 * firstOrDefault(myArray, x => x.age > 20) -> {id: 1, age: 31}
 * firstOrDetault(myArray, x => x.age == 31) -> {id: 1, age: 31}
 * firstOrDefault(myArray, x => x.age < 30) -> null
 * @param array 
 * @param predicate 
 */
export function firstOrDefault<T>(array: T[], predicate: (value: T) => boolean = null): T {
    if (array && predicate) {
        const results: Array<T> = array.filter(predicate);
        if (results.length == 0) {
            return null;
        }
        return results[0];
    }
    else {
        if (array && array.length > 0) {
            return array[0];
        }
    }
    return null;
}

/**
*
* Example myArray = [{id: 1, age: 31}, {id: 2, age: 25}]; sort(myArray, 'age') -> myArray[0].id === 2, myArray[1].id === 1;
* This method mutates the array and returns a reference to the same array.
* @param array
* @param prop 
* @param orderDirection 
* @param secondaryProp 
*/
export function sort<T>(array: T[], prop: string, orderDirection: OrderDirection = OrderDirection.ASC, secondaryProp: string = ""): T[] {
  return array.sort(function (a, b) {
        let valueA = getValue(a, prop),
            valueB = getValue(b, prop);
        
        if (secondaryProp) {
          var secondaryValueA = getValue(a, secondaryProp),
              secondaryValueB = getValue(b, secondaryProp);
        }

        switch (orderDirection) {
            case OrderDirection.ASC:
              if (valueA < valueB) return -1;
              if (valueA > valueB) return 1;
              if ((valueA === valueB) && secondaryProp) {
                if (secondaryValueA < secondaryValueB) return -1;
                if (secondaryValueA > secondaryValueB) return 1;
              }
            case OrderDirection.DESC:
              if (valueA > valueB) return -1;
              if (valueA < valueB) return 1;
              if ((valueA === valueB) && secondaryProp) {
                if (secondaryValueA > secondaryValueB) return -1;
                if (secondaryValueA < secondaryValueB) return 1;
              }
        }
    });

    function getValue(obj, prop) {
      return isNaN(parseInt(obj[prop])) ? obj[prop].toString().toLowerCase() : parseInt(obj[prop])
    }
}

/**
*
* Example myArray = [{id:1, date: 12-05-2019}, {id: 2, date: 30-01-2019}] ; sort(myArray, 'date', 'DD-MM-YYYY') -> myArray[0].id === 2, myArray[1].id === 1;
*/
export function sortByDate<T>(array: T[], dateProp: string, orderDirection: OrderDirection = OrderDirection.ASC, dateFormat: string): T[] {
    return array.sort(function (a, b) {
        let valueA = moment(a[dateProp], dateFormat);
        let valueB = moment(b[dateProp], dateFormat);
        switch (orderDirection) {
            case OrderDirection.ASC:
                return (valueA > valueB) ? 1 : ((valueB > valueA) ? -1 : 0);
            case OrderDirection.DESC:
                return (valueA < valueB) ? 1 : ((valueB < valueA) ? -1 : 0);
        }
    });
}

/**
 *
 * Example array1 = [{id:1, age: 31}, {id: 2, age: 25}, {id: 3, age: 30}, {id: 4, age: 40}], array2 = [{id: 2, age: 25}, {id: 3, age: 30}]
 * result = except(array1, array2, 'id') => result = [{id:1, age: 31}, {id: 4, age: 40}]
 */
export function except<T>(array1: T[], array2: T[], prop: string): T[] {
    if (array2 && array2.length) {
        return array1.filter(element1 => array2.findIndex(element2 => element2[prop] === element1[prop]) === -1);
    } else {
        return array1;
    }
}

/**
*
* Get max element in array by prop name;
* Example  myArray = [{id:1, age: 31}, {id: 2, age: 25}] ; max(myArray, 'age') -> 31
*/
export function max<T>(array: T[], prop: string): number {
    let temp = Number.MIN_VALUE;
    array.forEach(item => {
        if (item[prop] > temp) {
            temp = item[prop];
        }
    });

    return temp;
}

export function addOrUpdate<T>(array: T[], item: T, predicate: (value: T) => boolean = null) {
    var index = array.findIndex(predicate);
    if (index >= 0) {
        array[index] = item;
    } else {
        array.push(item);
    }
}

export function min<T>(array: T[], prop: string): number {
    let temp = Number.MAX_VALUE;
    array.forEach(item => {
        if (item[prop] < temp) {
            temp = item[prop];
        }
    });

    return temp;
}

/**
 * Checks if there is any element in array for provided condition predicate. If predicate is not provided, it checks only if array has any element.
 * Examples:
 * myArray = [{id: 2, name: 'Anna'}, {id: 3, name: 'Peter}];
 * any(myArray) -> true
 * any(myArray, element => element.name === 'Mike') -> false
 * any(myArray, element => element.name === 'Peter') -> true
 * 
 * @param array
 * @param predicate
 */
export function any<T>(array: T[], predicate: (value: T) => boolean = null): boolean {
    let result = array && array.length > 0;
    if (result && predicate) {
        const index = array.findIndex(predicate);
        result = index > -1;
    }

    return result;
}

/**
 * myArray = [{id: 2, name: 'Anna'}, {id: 3, name: 'Peter}] -> emptyArray(myArray);
 * @param array
 */
 export function emptyArray<T>(array: T[]): void {
    if (array) {
        array.splice(0, array.length);
    }
}

/**
 * Checks if number or string converted to number is an integer without leading zeros
 * @param value string or number to check
 */
export function isNumber(value) {
    if (value === '') { // to avoid error on empty inputs that are not required
        return true;
    }
    return isNaN(value) ? false : !hasLeadingZeros(value) && isFinite(value);
}

/**
 * Checks if number or string converted to number is decimal
 * @param value string or number to check
 */
export function isDecimal(value: any) {
    const num = parseFloat(value);
    return !isNumber(num) ? false : /^(0{1}|[1-9]{1,})\.\d{1,}$/.test(value);
}

/**
 * Checks for leading zeros
 * @param value string or number to check
 */
export function hasLeadingZeros(value: number) {
    const stringValue = value ? value.toLocaleString('en-US', { style: 'decimal' }) : '';
    return /^((0{2,}\.)|(0{1,}\d+\.?))\d*$/.test(stringValue);
}

/**
 * Removes html tags from provided text
 * @param text Text with html tags
 */
export function htmlToPlainText(text: string) {
    return text ? text.replace(/<[^>]+>/gm, '') : '';
}

export function contains<T>(array: T[], predicate: (value: T) => boolean): boolean {
    return !!array.find(predicate);
}

/**
 * If parameter is string it validates if it has value and does not starts with white-space.
 * If parameter is number if validates if it has value and it is not equal to 0.
 * @param value String which has to validate
 */
export function hasValue(value: string | number): boolean {
    if(value) {
        if(typeof(value) === 'string') {
            return !/^\s+$/.test(value);
        } else if(typeof(value) === 'number') {
            const numberValue: number = +value;
            return !!numberValue;
        }
    }

    return false;
}

/**
 * Validates if a string has leading or trailing whitespaces
 * @param text String for validation
 */
export function hasLeadingOrTrailingWhitespaces(text: string) {
  return text && (text.trim() !== text);
}

/**
 * Checks if two arrays are equal. Also checks item postion
 * @param array1 primitive type array
 * @param array2 primitive type array
 */
export function equal<T>(array1: T[], array2: T[]): boolean {
    if (array1?.length !== array2?.length) {
        return false;
    }

    for (let i = 0; i < array1.length; i++) {
        if (array1[i] !== array2[i]) {
            return false;
        }
    }

    return true;
}